<script setup lang="ts">
import { ProductAttributeFieldsService } from '@/general/services/attribute-fields/product-attribute-fields.service';
import { productAttributeFieldsServiceKey } from '@/general/services/attribute-fields/service-keys';
import { ToastService } from '@/general/services/toasts/toast.service';
import { TranslationService } from '@/general/services/translations/translation.service';
import AttributeFieldsRenderer from '@/general/ui/components/attribute-fields/attribute-fields-renderer.vue';
import { lockableKey } from '@/general/ui/components/attribute-fields/inject.keys';
import { CancelButton } from '@/general/ui/components/crud-buttons/ts/classes/cancel-crud-button.class';
import { CreateButton } from '@/general/ui/components/crud-buttons/ts/classes/create-crud-button.class';
import { CrudButtonPosition } from '@/general/ui/components/crud-buttons/ts/enums/crud-button-position.enum';
import { CrudButtonsOptions } from '@/general/ui/components/crud-buttons/ts/interfaces/crud-button-option.interface';
import BaseDialog from '@/general/ui/components/dialog/base-dialog.vue';
import AttributeSelect from '@/general/ui/components/selects/attribute-select.vue';
import LocaleSelect from '@/general/ui/components/selects/locale-select.vue';
import { ProductAttributeFieldError } from 'platform-unit2-api';
import { ActionRestService } from 'platform-unit2-api/actions';
import { Attribute, AttributeCompact, AttributeTypeEnum } from 'platform-unit2-api/attributes';
import { Locale } from 'platform-unit2-api/locales';
import { computed, onMounted, provide, Ref, ref } from 'vue';
import { useStore } from 'vuex';

/** Props */
interface Props {
  ids: number[];
  isActive: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  isActive: false,
  ids: () => [],
});

/** Emits */
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'hide'): void;
}>();

/** Services */
const toastService = ToastService.getInstance();
const ts = new TranslationService('supplier', 'products');
const actionApi = new ActionRestService();

/** Consts */
const store = useStore();
const attribute = ref<Attribute>();
const value = computed(
  () =>
    productAttributeFieldsService.value.updatedFields.find(
      (uf) => uf.attribute_id === attribute.value?.id,
    )?.value,
);
const selectedLocales = ref<Locale[]>([]);

onMounted(() => {
  const locales = store.getters['users/currentUser']?.workspace?.locales;
  if (store.getters['users/currentUser'] && locales) {
    selectedLocales.value = locales;
  }
});

const emptyFieldDefinition = () => {
  attribute.value = undefined;
};

const assign = async () => {
  if (value.value == null || !attribute.value) {
    return;
  }

  try {
    buttonsOptions.value.saving = true;
    const localeids = selectedLocales.value.map((locale) => locale.id);
    await actionApi.bulkUpdateFields(
      props.ids,
      localeids,
      attribute.value.id,
      value.value.toString(),
    );

    toastService.displaySuccessToast(
      ts.tModule('mass_update.success', {
        params: {
          field: attribute.value.key,
          amount: props.ids.length,
        },
      }),
    );
  } catch (err) {
    toastService.displayErrorToast(ts.tModule('mass_update.error'));
  } finally {
    emit('hide');
    buttonsOptions.value.saving = true;
  }
};

const cancelButton = new CancelButton({
  label: ts.tGlobal('cancel'),
  position: CrudButtonPosition.RIGHT,
  onClick: () => {
    emit('hide');
  },
});

const updateButton = new CreateButton({
  label: ts.tGlobal('update'),
  position: CrudButtonPosition.RIGHT,
  isEnabled: () => !!(attribute.value && value) && isFieldSupported(),
  onClick: assign,
});

const isFieldSupported = () => {
  if (!attribute.value) {
    return false;
  }

  return ![AttributeTypeEnum.GROUP_FIELD, AttributeTypeEnum.TAB_FIELD].includes(
    (attribute.value as unknown as AttributeCompact).options?.type,
  );
};

const buttonsOptions = ref<CrudButtonsOptions>({
  buttons: [cancelButton, updateButton],
});

const productAttributeFieldsService = ref(new ProductAttributeFieldsService());

provide(
  productAttributeFieldsServiceKey,
  productAttributeFieldsService as Ref<ProductAttributeFieldsService>,
);
provide(lockableKey, false);

const mockAttributeField = computed(() => ({
  attribute: { ...attribute.value, global: true } as any,
  children: [],
  path: null,
  values: [
    {
      id: 0,
      attribute_id: attribute.value?.id ?? 0,
      value: [],
      locale: { value: 'global', id: 0 },
      locked: false,
      path: null,
      errors: [] as ProductAttributeFieldError[],
    },
  ],
}));
</script>

<template>
  <BaseDialog
    :title="ts.tModule('mass_update.title')"
    :visible="isActive"
    :buttons-options="buttonsOptions"
    size="medium"
    @update:visible="emit('hide')"
  >
    <div class="field mb-3">
      <label class="font-bold">{{ ts.tGlobal('select_language') }}</label>
      <LocaleSelect v-model="selectedLocales" multiselect hide-label />
    </div>
    <div v-if="!attribute" class="field mb-3">
      <AttributeSelect v-model="attribute" />
      <small class="p-text-secondary">{{ ts.tModule('mass_update.select_field_message') }}</small>
    </div>

    <div v-else class="field mb-3">
      <div class="align-items-center flex justify-content-between">
        <p>
          {{
            ts.tModule('mass_update.updating_notification', {
              params: {
                field: attribute.key,
                amount: ids.length,
              },
            })
          }}
        </p>
        <Button
          outlined
          :label="ts.tModule('mass_update.change_field')"
          @click="emptyFieldDefinition"
        />
      </div>
      <div>
        <div
          v-if="[AttributeTypeEnum.GROUP_FIELD, AttributeTypeEnum.TAB_FIELD].includes((mockAttributeField.attribute as AttributeCompact)?.options?.type)"
        >
          Not supported
        </div>
        <AttributeFieldsRenderer v-else :attribute-fields="[mockAttributeField]" />
      </div>
    </div>
  </BaseDialog>
</template>
